<template>
  <div class="press-release">
    <div class="press-release__wrapper">
      <div
        class="press-release__header-background"
        :style="{
          backgroundImage: `url(${currentPressrelease.header_image})`,
        }"
      ></div>
      <div class="press-release__main-container">
        <div class="press-release__details-box">
          <div class="press-release__details-box_tags">
            <Tag
              class="tag--public"
              v-for="tag in currentPressrelease.tags"
              :key="tag.id"
              :value="tag.title"
              @click="
                $router.push({
                  path: '/content',
                  query: { tag_id: tag.id },
                })
              "
            />
          </div>
          <h2 class="press-release__details-box_title">
            {{ currentPressrelease.title }}
          </h2>
          <span v-if="currentPressrelease.date_published" class="press-release__details-box_data"
            >{{ currentPressrelease.date_published }} | by {{ currentPressrelease.author }} |
            {{ currentPressrelease.read_time }}</span
          >
          <div
            class="press-release__details-box_content collect-editor collect-editor--white"
            v-html="currentPressrelease.details"
          ></div>
          <BaseButton
            v-if="currentPressrelease.button_text && currentPressrelease.button_url"
            class="button button--xs button--white"
            :text="currentPressrelease.button_text"
            @click="goToLink(currentPressrelease.button_url)"
          />
        </div>

        <div v-if="randomList" class="press-release__title-box">
          <h2 class="press-release__title-box_title">More Press Releases</h2>
        </div>

        <div v-if="randomList" class="press-release__press-releases-box">
          <router-link
            v-for="(item, key) in randomList"
            :key="`${key}-item`"
            :to="{ path: `/media/press-releases/${item.slug}` }"
            v-slot="{ navigate }"
            custom
          >
            <div class="press-release__press-releases-box_card" @click="navigate">
              <div
                class="image"
                :style="{
                  backgroundImage: `url(${item.search_image})`,
                }"
              >
                <div class="image__preview"><p>Click to View</p></div>
              </div>
              <div class="type">PRESS RELEASE</div>
              <h3 class="title">
                {{ item.title }}
              </h3>
              <span class="data">{{ item.date_published }}</span>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import Tag from "@/components/Tag.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: { BaseButton, Tag },

  data() {
    return {};
  },
  head() {
    const title = this.currentPressrelease?.title ?? "Press Release browngrotta arts";
    const description =
      this.currentPressrelease?.details ?? "Description Press Release browngrotta arts";
    const image = this.currentPressrelease?.header_image ?? "/img/icons/favicon-500x500.png";

    return useSeoHead({
      title,
      description: `${title} - ${description}`,
      image,
      routePath: this.$route.path,
    });
  },
  computed: {
    ...mapGetters("publicapi/pressreleases", {
      currentPressrelease: "getCurrentPressrelease",
      randomList: "getRandomPressreleasesList",
    }),
  },
  async created() {
    await this.apiGetSpecificPressrelease(this.$route.params.slug);
    this.$emit("updateHead");
    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Media",
        to: "/media",
        clickable: true,
      },
      {
        title: "Press Releases",
        to: "/media/press-releases",
        clickable: true,
      },
      {
        title: `${this.currentPressrelease.title}`,
        to: `/media/press-releases/${this.$route.params.slug}`,
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);
  },

  watch: {
    "$route.path": async function () {
      await this.apiGetSpecificPressrelease(this.$route.params.slug);
      this.$emit("updateHead");
      const breadcrumbs = [
        {
          title: "Home",
          to: "/",
        },
        {
          title: "Media",
          to: "/media",
          clickable: true,
        },
        {
          title: "Press Releases",
          to: "/media/press-releases",
          clickable: true,
        },
        {
          title: `${this.currentPressrelease.title}`,
          to: `/media/press-releases/${this.$route.params.slug}`,
          clickable: false,
        },
      ];
      this.setBreadcrumbs(breadcrumbs);

      this.setShareData({
        title: this.currentPressrelease.title,
        description: this.currentPressrelease.details,
        image: this.currentPressrelease.header_image,
      });
    },
  },

  methods: {
    ...mapActions("publicapi/pressreleases", ["apiGetSpecificPressrelease"]),
    ...mapMutations("publicapi/share", ["setShareData"]),
    ...mapMutations(["setBreadcrumbs"]),

    goToLink(url) {
      window.open(url, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 18px;
  line-height: 1.3;
  @media screen and (max-width: $xs) {
    font-size: 16px;
    line-height: 1.5;
  }
}

.press-release {
  display: flex;

  &__header {
    &-background {
      @include backgroundBlock();
    }
  }

  &__wrapper {
    @include fullScreenWrapper;
  }

  &__main-container {
    @include mainContainerCenteredCol(36.2rem 10.5rem 30rem);

    @media screen and (max-width: $xs) {
      padding: 33.5rem 2.6rem 30rem;
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    width: 70%;
    @media screen and (max-width: $md) {
      width: 100%;
    }

    &_tags {
      display: flex;
      margin: 0 0 15px -10px;
    }

    &_title {
      font-size: 40px;
      line-height: 1.25;
      @media screen and (max-width: $xs) {
        font-size: 35px;
      }
    }

    &_data {
      font-size: 16px;
      line-height: 1.56;
      margin: 12px 0rem 135px;
      text-transform: uppercase;
      @media screen and (max-width: $xs) {
        font-size: 14px;
        margin: 7rem 0rem 2.8rem;
      }
    }

    .button {
      margin-top: 50px;
      min-width: 160px;
    }
  }

  &__title-box {
    width: 100%;
    margin: 20.2rem 0rem 6.5rem;
    @media screen and (max-width: $xs) {
      margin: 11rem 0rem 4rem;
    }

    &_title {
      font-size: 40px;
      line-height: 1.25;
      @media screen and (max-width: $xs) {
        font-size: 35px;
      }
    }
  }

  &__press-releases-box {
    @include gridLayoutWithCalc;

    &_card {
      @include flexColumnClickable;

      .image {
        position: relative;
        @include imageContainer();

        @media screen and (max-width: $xs) {
          height: 90vw;
        }

        &__preview {
          @include imagePreview;

          p {
            @include imagePreviewParagraph;
          }
        }

        &:hover {
          .image__preview {
            @include imageHoverEffect;
          }
        }
      }

      .type {
        @include typeBadge($marginTop: 26px, $fontSize: 14px, $lineHeight: 1.8);
      }

      .title {
        @include multilineEllipsisTitle;
      }

      .data {
        @include dataCaptionUppercase;
      }
    }
  }
}
</style>

